import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, Upload, Send, CheckCircle2, ArrowLeft, BookOpen, Download, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "@/components/SignaturePad";
import { MaskLine } from "@/components/Reveal";
import { API, formatApiErrorDetail, fileToDataUrl } from "@/lib/api";
import { COMPANY } from "@/lib/content";
import { HANDBOOK, HANDBOOK_UPDATED } from "@/lib/handbook";

const EMPTY = {
  full_name: "", email: "", phone: "", address: "", city_state_zip: "", ssn_last4: "",
  dob: "", position: "", start_date: "", emergency_name: "", emergency_phone: "", emergency_relation: "",
  filing_status: "", dependents: "", citizenship_status: "", bank_name: "", routing_number: "",
  account_number: "", handbook_ack: false, drug_free_ack: false, safety_ack: false,
  photo_file: "", signature_file: "",
  w4_multiple_jobs: false, w4_children_amt: "", w4_other_dependents_amt: "", w4_dependents_total: "",
  w4_other_income: "", w4_deductions: "", w4_extra_withholding: "", w4_exempt: false,
  i9_other_names: "", i9_citizenship: "", i9_uscis_number: "", i9_i94_number: "",
  i9_passport_number: "", i9_passport_country: "", i9_work_auth_expiration: "",
};

function Field({ label, value, onChange, testid, type = "text", required, ...rest }) {
  return (
    <div>
      <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{label}{required && <span className="text-wcc-red"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} required={required} {...rest}
        className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
    </div>
  );
}

function SectionTitle({ n, children }) {
  return (
    <div className="flex items-center gap-3 mb-6 mt-2">
      <span className="mono text-xs text-wcc-red">{n}</span>
      <h3 className="font-display text-xl font-medium text-wcc-ink">{children}</h3>
      <span className="flex-1 h-px bg-wcc-ink/10" />
    </div>
  );
}

function HandbookReader() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-wcc-ink/10 border-t-4 border-t-wcc-red p-6 sm:p-8" data-testid="handbook-reader">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 grid place-items-center bg-wcc-navy text-white shrink-0"><BookOpen className="w-6 h-6" /></div>
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-wcc-red mb-1">Step 1 · Read First</p>
            <h2 className="font-display text-2xl font-medium text-wcc-ink leading-tight">Employee Handbook</h2>
            <p className="text-sm text-wcc-ink/55 mt-1">{HANDBOOK_UPDATED} · Please review before completing your registration.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href="/handbook/handbook.docx" download data-testid="handbook-download"
            className="inline-flex items-center gap-2 bg-wcc-red text-white font-semibold px-4 py-2.5 rounded-sm hover:bg-wcc-red-deep transition-colors text-sm">
            <Download className="w-4 h-4" /> Download
          </a>
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid="handbook-toggle"
            className="inline-flex items-center gap-2 border border-wcc-ink/20 text-wcc-ink font-semibold px-4 py-2.5 rounded-sm hover:border-wcc-red hover:text-wcc-red transition-colors text-sm">
            {open ? "Hide" : "Read Here"} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-6 max-h-[420px] overflow-y-auto pr-3 border-t border-wcc-ink/10 pt-5 space-y-6" data-testid="handbook-body">
          {HANDBOOK.map((s) => (
            <div key={s.title}>
              <h4 className="font-display text-base font-semibold text-wcc-ink mb-2">{s.title}</h4>
              {s.body.map((p, i) => (
                <p key={i} className="text-sm text-wcc-ink/70 leading-relaxed mb-2">{p}</p>
              ))}
            </div>
          ))}
          <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray pt-2">
            This is a summary. Download the full handbook above for all policies.
          </p>
        </div>
      )}
    </div>
  );
}

const CITIZENSHIP = [
  { v: "citizen", label: "A citizen of the United States" },
  { v: "noncitizen_national", label: "A noncitizen national of the United States" },
  { v: "permanent_resident", label: "A lawful permanent resident" },
  { v: "authorized_alien", label: "An alien authorized to work" },
];

function Gate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/employee/verify-gate`, { password: pw });
      sessionStorage.setItem("wcc_gate", data.token);
      onUnlock(data.token);
      toast.success("Access granted. Welcome aboard.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[100svh] grid place-items-center bg-wcc-navy px-5 relative overflow-hidden wcc-noise">
      <img src="/assets/field-1.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
      <div className="absolute inset-0 bg-wcc-navy/60" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-white/50 hover:text-white mb-8 transition-colors" data-testid="gate-back">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="bg-white p-8 sm:p-10 border-t-4 border-wcc-red">
          <div className="w-14 h-14 grid place-items-center bg-wcc-navy text-white mb-6"><Lock className="w-7 h-7" /></div>
          <h1 className="font-display text-3xl font-semibold text-wcc-ink tracking-tight mb-2">Employee Portal</h1>
          <p className="text-wcc-ink/60 mb-8 leading-relaxed">
            Enter the access password provided by West Coral Communications to begin your onboarding.
          </p>
          <form onSubmit={submit} data-testid="gate-form">
            <Field label="Access Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} testid="gate-password" required />
            <button type="submit" disabled={loading} data-testid="gate-submit"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-wcc-red text-white font-semibold px-6 py-4 rounded-sm hover:bg-wcc-red-deep disabled:opacity-60 transition-colors">
              {loading ? "Verifying…" : <>Unlock Onboarding <ShieldCheck className="w-4 h-4" /></>}
            </button>
          </form>
          <p className="mt-6 mono text-[10px] uppercase tracking-widest text-wcc-gray text-center">
            Don’t have the password? Contact {COMPANY.recruitingContact.email}
          </p>
        </div>
      </div>
    </div>
  );
}

function OnboardingForm({ token }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChk = (k) => (v) => setForm((f) => ({ ...f, [k]: !!v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Image too large (max 6MB).");
    const data = await fileToDataUrl(file);
    setForm((f) => ({ ...f, photo_file: data }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name) return toast.error("Please enter your full name.");
    if (!form.signature_file) return toast.error("A signature is required.");
    if (!form.handbook_ack || !form.drug_free_ack || !form.safety_ack)
      return toast.error("Please acknowledge all policies.");
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding`, form, { headers: { Authorization: `Bearer ${token}` } });
      setDone(true);
      toast.success("Onboarding submitted successfully!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-[100svh] grid place-items-center bg-wcc-paper px-5 pt-[74px]">
        <div className="max-w-lg text-center">
          <div className="w-20 h-20 grid place-items-center bg-wcc-red text-white mx-auto mb-8"><CheckCircle2 className="w-10 h-10" /></div>
          <h1 className="font-display text-4xl font-semibold text-wcc-ink tracking-tight mb-4">You’re all set.</h1>
          <p className="text-wcc-ink/65 leading-relaxed mb-8">
            Your onboarding form has been submitted to West Coral Communications. A manager will review your
            information and follow up with next steps.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-wcc-navy text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-wcc-red transition-colors" data-testid="onboarding-home-btn">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[74px] bg-wcc-paper min-h-screen">
      <section className="bg-wcc-navy text-white py-16">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-4">New Employee Registration</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter uppercase leading-none">
            <MaskLine delay={0.1}>Welcome to the</MaskLine>
            <MaskLine delay={0.22} className="text-wcc-red">West Coral crew.</MaskLine>
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl">Complete your onboarding registration below. All information is confidential.</p>
        </div>
      </section>

      <section className="py-16">
        <form onSubmit={submit} className="max-w-[900px] mx-auto px-5 sm:px-8 space-y-6" data-testid="onboarding-form">
          <HandbookReader />
          <div className="bg-white border border-wcc-ink/10 p-8 sm:p-12">
            <SectionTitle n="01">Personal Information</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Legal Name" required value={form.full_name} onChange={set("full_name")} testid="ob-full-name" />
              <Field label="Position" value={form.position} onChange={set("position")} testid="ob-position" />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="ob-email" />
              <Field label="Phone" value={form.phone} onChange={set("phone")} testid="ob-phone" />
              <Field label="Date of Birth" value={form.dob} onChange={set("dob")} testid="ob-dob" placeholder="MM/DD/YYYY" />
              <Field label="Anticipated Start Date" value={form.start_date} onChange={set("start_date")} testid="ob-start" />
              <Field label="Home Address" value={form.address} onChange={set("address")} testid="ob-address" />
              <Field label="City, State, ZIP" value={form.city_state_zip} onChange={set("city_state_zip")} testid="ob-csz" />
              <Field label="SSN (last 4)" value={form.ssn_last4} onChange={set("ssn_last4")} testid="ob-ssn" maxLength={4} />
            </div>

            <SectionTitle n="02">Emergency Contact</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Contact Name" value={form.emergency_name} onChange={set("emergency_name")} testid="ob-em-name" />
              <Field label="Contact Phone" value={form.emergency_phone} onChange={set("emergency_phone")} testid="ob-em-phone" />
              <Field label="Relationship" value={form.emergency_relation} onChange={set("emergency_relation")} testid="ob-em-rel" />
            </div>

            <SectionTitle n="03">Tax Withholding — IRS Form W-4</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Step 1 · Filing Status (Single / Married Filing Jointly / Head of Household)" value={form.filing_status} onChange={set("filing_status")} testid="ob-filing" />
              <div className="flex items-end pb-1">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.w4_multiple_jobs} onCheckedChange={setChk("w4_multiple_jobs")} data-testid="ob-w4-multiple" className="mt-0.5 data-[state=checked]:bg-wcc-red data-[state=checked]:border-wcc-red" />
                  <span className="text-sm text-wcc-ink/80 leading-relaxed">Step 2 · Multiple jobs or spouse also works</span>
                </label>
              </div>
            </div>
            <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray mt-6 mb-3">Step 3 · Claim Dependents</p>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Qualifying children under 17 × $2,000" value={form.w4_children_amt} onChange={set("w4_children_amt")} testid="ob-w4-children" />
              <Field label="Other dependents × $500" value={form.w4_other_dependents_amt} onChange={set("w4_other_dependents_amt")} testid="ob-w4-otherdep" />
              <Field label="Total credits ($)" value={form.w4_dependents_total} onChange={set("w4_dependents_total")} testid="ob-w4-deptotal" />
            </div>
            <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray mt-6 mb-3">Step 4 · Other Adjustments (optional)</p>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="(a) Other income ($)" value={form.w4_other_income} onChange={set("w4_other_income")} testid="ob-w4-income" />
              <Field label="(b) Deductions ($)" value={form.w4_deductions} onChange={set("w4_deductions")} testid="ob-w4-deductions" />
              <Field label="(c) Extra withholding ($)" value={form.w4_extra_withholding} onChange={set("w4_extra_withholding")} testid="ob-w4-extra" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer mt-5">
              <Checkbox checked={form.w4_exempt} onCheckedChange={setChk("w4_exempt")} data-testid="ob-w4-exempt" className="mt-0.5 data-[state=checked]:bg-wcc-red data-[state=checked]:border-wcc-red" />
              <span className="text-sm text-wcc-ink/80 leading-relaxed">I claim exemption from withholding for this year (I meet both conditions on Form W-4).</span>
            </label>

            <SectionTitle n="04">Employment Eligibility — USCIS Form I-9 (Section 1)</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <Field label="Other last names used (if any)" value={form.i9_other_names} onChange={set("i9_other_names")} testid="ob-i9-othernames" />
            </div>
            <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray mb-3">I attest, under penalty of perjury, that I am (check one):</p>
            <div className="space-y-2.5">
              {CITIZENSHIP.map((c) => (
                <label key={c.v} className="flex items-center gap-3 cursor-pointer border border-wcc-ink/15 hover:border-wcc-red px-4 py-3 transition-colors" data-testid={`ob-i9-${c.v}`}>
                  <input type="radio" name="i9_citizenship" value={c.v} checked={form.i9_citizenship === c.v}
                    onChange={(e) => setForm((f) => ({ ...f, i9_citizenship: e.target.value }))}
                    className="accent-wcc-red w-4 h-4" />
                  <span className="text-sm text-wcc-ink/85">{c.label}</span>
                </label>
              ))}
            </div>
            {form.i9_citizenship === "permanent_resident" && (
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="USCIS / A-Number" value={form.i9_uscis_number} onChange={set("i9_uscis_number")} testid="ob-i9-uscis" />
              </div>
            )}
            {form.i9_citizenship === "authorized_alien" && (
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="Work authorization expiration (MM/DD/YYYY)" value={form.i9_work_auth_expiration} onChange={set("i9_work_auth_expiration")} testid="ob-i9-exp" />
                <Field label="USCIS / A-Number" value={form.i9_uscis_number} onChange={set("i9_uscis_number")} testid="ob-i9-uscis2" />
                <Field label="Form I-94 Admission Number" value={form.i9_i94_number} onChange={set("i9_i94_number")} testid="ob-i9-i94" />
                <Field label="Foreign Passport Number" value={form.i9_passport_number} onChange={set("i9_passport_number")} testid="ob-i9-passport" />
                <Field label="Country of Issuance" value={form.i9_passport_country} onChange={set("i9_passport_country")} testid="ob-i9-country" />
              </div>
            )}

            <SectionTitle n="05">Direct Deposit</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Bank Name" value={form.bank_name} onChange={set("bank_name")} testid="ob-bank" />
              <Field label="Routing Number" value={form.routing_number} onChange={set("routing_number")} testid="ob-routing" />
              <Field label="Account Number" value={form.account_number} onChange={set("account_number")} testid="ob-account" />
            </div>

            <SectionTitle n="06">Photo / ID</SectionTitle>
            <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
              <div className="w-28 h-28 border-2 border-wcc-ink/15 bg-wcc-paper grid place-items-center overflow-hidden">
                {form.photo_file ? <img src={form.photo_file} alt="preview" className="w-full h-full object-cover" /> : <Upload className="w-6 h-6 text-wcc-ink/30" />}
              </div>
              <label className="cursor-pointer">
                <div className="inline-flex items-center gap-2 border-2 border-dashed border-wcc-ink/20 hover:border-wcc-red px-5 py-3 transition-colors">
                  <Upload className="w-4 h-4 text-wcc-red" />
                  <span className="text-sm text-wcc-ink/70">Upload a photo or ID image (max 6MB)</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="ob-photo" />
              </label>
            </div>

            <SectionTitle n="07">Signature</SectionTitle>
            <p className="text-sm text-wcc-ink/60 mb-3">By signing below, I certify the information provided is accurate.</p>
            <SignaturePad onChange={(d) => setForm((f) => ({ ...f, signature_file: d }))} testid="ob-signature" />

            <SectionTitle n="08">Acknowledgements</SectionTitle>
            <div className="space-y-4">
              {[
                ["handbook_ack", "I have received and agree to the Employee Handbook."],
                ["drug_free_ack", "I acknowledge the drug-free workplace policy and consent to screening where required."],
                ["safety_ack", "I agree to follow all OSHA, jobsite, and company safety requirements."],
              ].map(([key, text]) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form[key]} onCheckedChange={setChk(key)} data-testid={`ob-${key}`} className="mt-0.5 data-[state=checked]:bg-wcc-red data-[state=checked]:border-wcc-red" />
                  <span className="text-sm text-wcc-ink/80 leading-relaxed">{text}</span>
                </label>
              ))}
            </div>

            <button type="submit" disabled={loading} data-testid="onboarding-submit"
              className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-wcc-red text-white font-semibold px-6 py-4 rounded-sm hover:bg-wcc-red-deep disabled:opacity-60 transition-colors">
              {loading ? "Submitting…" : <>Submit Onboarding <Send className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default function Onboarding() {
  const [token, setToken] = useState(() => sessionStorage.getItem("wcc_gate") || "");
  if (!token) return <Gate onUnlock={setToken} />;
  return <OnboardingForm token={token} />;
}
